import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Plus, Upload } from "lucide-react";
import { getDealDetail } from "@/lib/deals.functions";
import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { RankingDemoBanner } from "@/components/ranking-demo-banner";
import { ContactsSection } from "@/components/deal-contacts/contacts-section";
import { AddContactDialog } from "@/components/deal-contacts/add-contact-dialog";
import { ImportContactsDialog } from "@/components/deal-contacts/import-contacts-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";

interface ContactsSearch {
  /** Land with the Replied filter preselected (Today block link). */
  replied?: "1";
}

export const Route = createFileRoute("/_authed/deals/$dealId_/contacts")({
  validateSearch: (search: Record<string, unknown>): ContactsSearch =>
    search.replied === "1" || search.replied === 1 ? { replied: "1" } : {},
  head: () => ({
    meta: [
      { title: "Deal contacts: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DealContactsPage,
});

function DealContactsPage() {
  const { dealId } = Route.useParams();
  const { replied } = Route.useSearch();
  const fetchDetail = useServerFn(getDealDetail);
  const detail = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
  });
  const hasContacts =
    (detail.data as { has_delivered_contacts?: boolean } | undefined)
      ?.has_delivered_contacts === true;
  const rankingDemo =
    (detail.data as { ranking_demo?: { category: string; stage: string } | null } | undefined)
      ?.ranking_demo ?? null;

  return (
    <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
      {rankingDemo && (
        <RankingDemoBanner
          category={rankingDemo.category}
          stage={rankingDemo.stage}
        />
      )}
      <DealPageHeader dealId={dealId} />
      <DealNav
        dealId={dealId}
        active="contacts"
        isDemo={(detail.data?.deal as { is_demo?: boolean } | undefined)?.is_demo === true}
      />
      {detail.isLoading ? null : hasContacts ? (
        <ContactsSection dealId={dealId} initialRepliedOnly={replied === "1"} />
      ) : (
        <ContactsEmptyState dealId={dealId} />
      )}
    </main>
  );
}

function ContactsEmptyState({ dealId }: { dealId: string }) {
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  return (
    <>
      <EmptyState
        title="No contacts yet"
        description="Investor and acquirer contacts will appear here once CapitalDart delivers your matched list. You can also add or import contacts manually at any time."
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAddOpen(true)}
          className="gap-1.5"
        >
          <Plus className="size-3.5" />
          Add contact
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setImportOpen(true)}
          className="gap-1.5"
        >
          <Upload className="size-3.5" />
          Import contacts
        </Button>
      </EmptyState>
      <AddContactDialog dealId={dealId} open={addOpen} onOpenChange={setAddOpen} />
      <ImportContactsDialog dealId={dealId} open={importOpen} onOpenChange={setImportOpen} />
    </>
  );
}
